import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PreferencesSection } from './PreferencesSection';
import subscriptionService from '../../services/subscriptionService';
import { toast } from 'react-hot-toast';
import { ServiceType } from '../../types/api';

interface PreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscriptionId: string;
  serviceType?: ServiceType;
}

const PreferencesModal: React.FC<PreferencesModalProps> = ({
  isOpen,
  onClose,
  subscriptionId,
  serviceType = 'flower'
}) => {
  const { t } = useTranslation();
  const [localPreferences, setLocalPreferences] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Load initial preferences
  useEffect(() => {
    if (isOpen && subscriptionId) {
      loadPreferences();
    }
  }, [isOpen, subscriptionId]);

  const loadPreferences = async () => {
    try {
      setIsLoading(true);
      const response = await subscriptionService.getSubscriptionPreferences(subscriptionId);
      setLocalPreferences(response.preferences || {});
    } catch (error) {
      console.error('Error loading preferences:', error);
      toast.error(t('subscription.preferences.fetchError', 'Failed to load preferences'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreferencesChange = (newPreferences: Record<string, boolean>) => {
    setLocalPreferences(newPreferences);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await subscriptionService.updateSubscriptionPreferences(subscriptionId, localPreferences);
      toast.success(t('subscription.preferences.saveSuccess', 'Preferences saved successfully'));
      onClose();
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error(t('subscription.preferences.saveError', 'Failed to save preferences'));
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center">
      <div className="relative bg-white w-full h-full md:max-w-md md:max-h-[90vh] md:rounded-lg md:shadow-xl flex flex-col">
        {/* Header */}
        <div className="bg-white p-4 flex items-center justify-between sticky top-0 z-10 border-b">
          <button
            onClick={onClose}
            className="p-0 bg-transparent border-none"
          >
            <X size={24} />
          </button>
          <h1 className="text-md font-medium">
            {serviceType === 'fruit' 
              ? t('subscription.fruitPreferences.title', 'Fruit Preferences')
              : t('subscription.preferences.title', 'Flower Preferences')}
          </h1>
          <div className="w-6"></div> {/* Placeholder for balance */}
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto pb-20">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-yellow-400"></div>
            </div>
          ) : (
            <PreferencesSection 
              preferences={localPreferences} 
              onChange={handlePreferencesChange} 
              serviceType={serviceType}
            />
          )}
        </div>
        
        {/* Bottom buttons - sticky to modal bottom */}
        <div className="sticky bottom-0 w-full bg-white border-t border-gray-200 p-4 pb-8 mt-auto">
          <button 
            onClick={handleSave}
            disabled={isSaving}
            className="w-full bg-amber-300 text-black font-semibold p-4 rounded-full m-0"
          >
            {isSaving 
              ? t('common.saving', 'Saving...') 
              : t('common.save', 'Save Preferences')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreferencesModal; 